import { isNullOrUndefined } from 'common/util/type/typeUtil';
import { UserPrivileges } from 'common/store/session/constants';
import { fixNumberDecimals } from 'common/util/utils';
import * as thresholdCustomFields from '../../../../docs/definitions/thresholdCustomFields.json';
import { Threshold } from '../reducer';
import { getCurrentUserPrivileges, hasCurrentUserEvaluatorPrivilege } from '../../../services/permissions/PermissionService';
import { isApParentTest } from '../../../services/assessmentService';

// since we show 2 decimals, make sense to define step as 0.01
export const THRESHOLD_VALUE_STEP = 0.01;

export const getColumns = () => {
    const userPrivileges = getCurrentUserPrivileges();
    const columns = thresholdCustomFields.default ? thresholdCustomFields.default : thresholdCustomFields;
    return columns.filter((column: any) => {
        // Percentage correct column should not be visible for ap parent
        if (column.ref === 'correctPercentage' && isApParentTest()) {
            return false;
        }

        if (isNullOrUndefined(column.privilege)) {
            return true;
        }
        return column.privilege.some((priv: UserPrivileges) => userPrivileges.indexOf(priv) > -1);
    });
};

// used when has overridden threshold
export const updateStatistics = (
    initialThresholds: any[],
    oldThresholds: any[],
    updatedThresholds: any[],
    newValue: number,
    thresholdId: string,
    testMaxScore: number,
    canNotDiscardThresholds: boolean,
) => {
    const isGrader = hasCurrentUserEvaluatorPrivilege();
    let revertingChanges = false;
    const newThresholds: any[] = [];
    oldThresholds.forEach((threshold: any) => {
        const currentThreshold = { ...threshold };
        const prevThreshold = newThresholds.length > 0 ? newThresholds[newThresholds.length - 1] : undefined;
        let startValue = parseFloat(currentThreshold.startValue);
        let thresholdValue = parseFloat(currentThreshold.threshold);
        const thresholdValueForPercentageCalculation = thresholdValue;
        let newThresholdValue = parseFloat(currentThreshold.threshold);
        currentThreshold.thresholdHasError = false;
        let newThresholdValueForPercentageCalculation = newThresholdValue;

        if (parseInt(thresholdId, 10) === 1) {
            // if the max score changes because of weights,
            // there is no way to know if the old value was lower or higher than the current one
            // so just use the testMaxScore as the threshold for the highest grade
            newThresholdValue = testMaxScore;
            newThresholdValueForPercentageCalculation = newThresholdValue;
        }

        if (prevThreshold !== undefined) {
            newThresholdValue = parseFloat(prevThreshold.startValue);
            newThresholdValueForPercentageCalculation = newThresholdValue;
            const criteriaValue = currentThreshold.id === thresholdId ? newValue : startValue;
            const padderValue = testMaxScore !== criteriaValue ? THRESHOLD_VALUE_STEP : 0;
            thresholdValue = newThresholdValue - padderValue;

            if (!isNullOrUndefined(prevThreshold.initialMark) && prevThreshold.initialMark !== prevThreshold.startValue) {
                thresholdValue = prevThreshold.initialMark - padderValue;
            }

            if (newThresholdValue <= criteriaValue) {
                newThresholds[newThresholds.length - 1].thresholdHasError = true;
                currentThreshold.thresholdHasError = true;
            }
            newThresholdValue -= padderValue;
            const percentageCorrectStart = (startValue / testMaxScore) * 100;
            const percentageCorrectEnd = ((thresholdValueForPercentageCalculation / testMaxScore) * 100) - THRESHOLD_VALUE_STEP;
            const newPercentageCorrectEnd = ((newThresholdValueForPercentageCalculation / testMaxScore) * 100) - THRESHOLD_VALUE_STEP;
            let ofCorrect = `${fixNumberDecimals(percentageCorrectStart, 2)}% - ${fixNumberDecimals(newPercentageCorrectEnd, 2)}% `;

            if (thresholdValue !== newThresholdValue && !canNotDiscardThresholds) {
                ofCorrect = `${ofCorrect} (${fixNumberDecimals(percentageCorrectEnd, 2)}%)`;
            }
            let fromTo = `${fixNumberDecimals(startValue, 2)}`;
            if (newThresholdValue === thresholdValue) {
                fromTo = `${fromTo} - ${fixNumberDecimals(thresholdValue, 2)}`;
            } else {
                fromTo = `${fromTo} - ${fixNumberDecimals(newThresholdValue, 2)} (${fixNumberDecimals(thresholdValue, 2)}) `;
            }

            currentThreshold.fromToScores = fromTo;
            currentThreshold.ofCorrect = ofCorrect;
        }

        if (thresholdId === currentThreshold.id) {
            if (newValue > testMaxScore) {
                currentThreshold.thresholdHasError = true;
            }
            revertingChanges = currentThreshold.initialMark === newValue;
            if (!currentThreshold.label.includes('*')) {
                currentThreshold.label += '*';
            }
            if (revertingChanges) {
                currentThreshold.label = currentThreshold.label.substr(0, currentThreshold.label.length - 1);
                currentThreshold.startValue = currentThreshold.initialMark;
                startValue = currentThreshold.initialMark;
                delete currentThreshold.initialMark;
            }
            // need the initial threshold to show proper old values in parenthesis
            const initialThreshold = initialThresholds.find((oldThreshold: any) => {
                return oldThreshold.id === currentThreshold.id;
            });
            const percentageCorrectStart = (initialThreshold.startValue / testMaxScore) * 100;
            const step = prevThreshold !== undefined ? THRESHOLD_VALUE_STEP : 0;
            const newPercentageCorrectStart = (newValue / testMaxScore) * 100;
            // when the weights change and it's calculating using the old values it can go over 100% which is invalid
            const percentageCorrectEnd = ((thresholdValueForPercentageCalculation / testMaxScore) * 100) - step;
            let ofCorrect = `${fixNumberDecimals(percentageCorrectStart, 2)}% `;
            let fromTo = `${fixNumberDecimals(startValue, 2)}`;
            if (newValue !== startValue) {
                fromTo = `${fixNumberDecimals(newValue, 2)} (${fixNumberDecimals(initialThreshold.startValue, 2)})`;
                ofCorrect = `${fixNumberDecimals(newPercentageCorrectStart, 2)}%`;
                if (!canNotDiscardThresholds) {
                    ofCorrect = `${ofCorrect} (${fixNumberDecimals(percentageCorrectStart, 2)}%)`;
                }
            }

            if (newThresholdValue === thresholdValue) {
                fromTo = `${fromTo} - ${fixNumberDecimals(thresholdValue, 2)}`;
                ofCorrect = `${ofCorrect} - ${fixNumberDecimals(percentageCorrectEnd, 2)}%`;
            } else {
                fromTo = `${fromTo} - ${fixNumberDecimals(newThresholdValue, 2)} (${fixNumberDecimals(thresholdValue, 2)}) `;
                const newPercentageCorrectEnd = ((newThresholdValueForPercentageCalculation / testMaxScore) * 100) - step;
                ofCorrect = `${ofCorrect} - ${fixNumberDecimals(newPercentageCorrectEnd, 2)}%`;
                if (!canNotDiscardThresholds) {
                    ofCorrect = ` ${ofCorrect} (${fixNumberDecimals(percentageCorrectEnd, 2)}%)`;
                }
            }

            currentThreshold.fromToScores = fromTo;
            currentThreshold.ofCorrect = ofCorrect;

            currentThreshold.startValue = newValue;
            if (newValue !== startValue && isNullOrUndefined(currentThreshold.initialMark)) {
                currentThreshold.initialMark = startValue;
            }
        }
        if (isGrader) {
            const updatedThreshold = updatedThresholds.find((updatedThreshold: any) => updatedThreshold.id === currentThreshold.id);
            if (updatedThreshold) {
                let ofTotal = 'N/A';
                if (!isNaN(updatedThreshold.percent)) {
                    ofTotal = `${fixNumberDecimals(updatedThreshold.percent, 2)}%`;
                }
                currentThreshold.candidates = {
                    ...currentThreshold.candidates,
                    updatedValue: updatedThreshold.count,
                };
                currentThreshold.ofTotal = {
                    ...currentThreshold.ofTotal,
                    updatedValue: ofTotal,
                };
                currentThreshold.avgScores = getAvailableAvgScores(
                    currentThreshold.candidates.currentValue as number,
                    updatedThreshold.count,
                    currentThreshold.avgScores.currentValue as number,
                    updatedThreshold.averageScore,
                );
            }
        }
        newThresholds.push(currentThreshold);
    });
    newThresholds[newThresholds.length - 1].disabled = true;
    return newThresholds;
};

const getAvailableAvgScores = (curCandidates: number, updCandidates: number, currentValue: number, newAverageScore: number) => {
    const avgScores: any = {
        currentValue,
        updatedValue: newAverageScore,
    };
    if (curCandidates === 0) {
        avgScores.currentValue = 'N/A';
    }
    if (updCandidates === 0) {
        avgScores.updatedValue = 'N/A';
    }
    return avgScores;
};

// used when has non-overridden threshold
export const computeStatistics = (
    thresholds: Threshold[],
    emulatedThresholds: any[],
    testMaxScore: number,
): Threshold[] => {
    const isGrader = hasCurrentUserEvaluatorPrivilege();
    return thresholds.map((threshold: Threshold, index: number) => {
        let stats: any = {};
        const startValue = threshold.startValue;
        let endValue = index !== 0 ? thresholds[index - 1]?.startValue : testMaxScore;
        const endValueForPercentage = endValue;
        if (index !== 0 && startValue !== testMaxScore) {
            endValue -= 0.01;
        }
        const fromToScores = [fixNumberDecimals(startValue, 2), fixNumberDecimals(endValue, 2)];
        if (isGrader) {
            const emulatedThreshold = emulatedThresholds.find((emulatedThreshold: Threshold) => emulatedThreshold.id === threshold.id);
            let ofTotal = 'N/A';
            if (!isNaN(emulatedThreshold.percent)) {
                ofTotal = `${fixNumberDecimals(emulatedThreshold.percent, 2)}%`;
            }
            stats = {
                ofTotal: {
                    currentValue: ofTotal,
                },
                avgScores: {
                    currentValue: isNullOrUndefined(emulatedThreshold.averageScore) ? 'N/A' : emulatedThreshold.averageScore,
                },
                candidates: {
                    currentValue: emulatedThreshold.count,
                },
            };
        }
        const percentageCorrectStart = (startValue / testMaxScore) * 100;
        const step = endValueForPercentage === testMaxScore ? 0 : THRESHOLD_VALUE_STEP;
        const percentageCorrectEnd = ((endValueForPercentage / testMaxScore) * 100) - step;
        const ofCorrect = `${fixNumberDecimals(percentageCorrectStart, 2)}% - ${fixNumberDecimals(percentageCorrectEnd, 2)}%`;
        return {
            ...threshold,
            ...stats,
            fromToScores: fromToScores.join(' - '),
            ofCorrect,
            ...(index === (thresholds.length - 1) ? { disabled: true } : {}),
        };
    });
};

export const getThresholdsChanges = (initialThresholds: Threshold[], currentThresholds: Threshold[]) => {
    const changes = currentThresholds.filter((threshold: any) => {
        const initialThreshold = initialThresholds.find((initialThresh: any) => initialThresh.id === threshold.id);
        return initialThreshold.startValue !== threshold.startValue
            || (parseInt(threshold.id, 10) === 1 && initialThreshold.threshold !== threshold.threshold);
    });
    return changes;
};
